import axios from 'axios';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import useSWR, { useSWRInfinite } from 'swr';
import { VIP_CONFIG, PAGE_SIZE_CONFIG } from '../brands/default/config';
import {
  PREFORMATTED_STRING,
  BOOKED,
  CLICK_REDEEM_IT,
  READY_TO_REDEEM,
} from '../brands/default/content/helpers';
import { SHOW_GET_VOUCHER_BUTTON } from '../brands/default/features';
import {
  URLVOUCHERFEED,
  URL_VOUCHER_EXTENSION,
  URL_PROTECT_MY_VOUCHER_REFUND_DATA,
} from '../brands/default/services';
import { fetchVoucherPage } from '../services/vouchers';
import {
  isNewPurchasesAffiliate,
  trackPurchaseAffiliate,
  isNewClient,
} from './affiliate';
import httpCommonHeaders from './httpCommonHeaders';

export const useVoucherFeed = (id) => {
  const orderId = Number(id);
  const [working, setWorking] = useState(false);
  const { data, size, setSize, isValidating, mutate } = useSWRInfinite(
    URLVOUCHERFEED,
    fetchVoucherPage,
    {
      dedupingInterval: 600_000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const ready = Boolean(data);
  const lastPageFull = Boolean(
    data?.[data.length - 1]?.length === PAGE_SIZE_CONFIG.VOUCHER_FEED,
  );

  useEffect(() => {
    if (!isValidating) {
      setWorking(false);
    }
  }, [isValidating]);

  const visibleVouchers =
    data?.flat().filter((voucher) => {
      // FIXME: we should get BE to identify sub deals like we do with giftPack
      return (
        !voucher.giftPack &&
        voucher.giftType !== 'gift-wrap' &&
        voucher.dealId !== VIP_CONFIG.VIP_SUBSCRIPTION_DEAL_ID
      );
    }) || [];

  const vouchers = visibleVouchers.filter(
    (voucher) => voucher.productOrderId !== orderId,
  );
  const purchases = visibleVouchers.filter(
    (voucher) => voucher.productOrderId === orderId,
  );

  return {
    canLoadMore: !working && lastPageFull,
    count: vouchers.length + purchases.length,
    endOfFeed: ready && !lastPageFull,
    loadMore: () => {
      setWorking(true);
      setSize(size + 1);
    },
    mutate,
    purchases,
    ready,
    vouchers,
    working,
  };
};

export const useVoucherExtensionFeed = (productOrderId) => {
  let url = `${URL_VOUCHER_EXTENSION}`;
  if (productOrderId) url += `?productOrderId=${productOrderId}`;
  const { data, mutate } = useSWR(url, fetchVoucherPage);
  const extensions = data;
  const mutateExtensions = mutate;

  return {
    extensions,
    mutateExtensions,
  };
};

export const getVoucherStatus = ({
  deliveryStatus,
  voucherSentDate,
  flightIntegration,
}) => {
  if (flightIntegration) {
    return BOOKED;
  }
  if (
    (Boolean(voucherSentDate) &&
      deliveryStatus
        .toLowerCase()
        .includes(
          `this ${PREFORMATTED_STRING.brandTerminology.wowcher_or_voucher} can be printed from`,
        )) ||
    deliveryStatus.toLowerCase() === 'view'
  ) {
    return READY_TO_REDEEM;
  }

  return deliveryStatus;
};

export const isVoucherRedeemable = ({ orderLineStatus, expired }) => {
  return (
    ['payment_captured', 'redeemed'].includes(orderLineStatus.toLowerCase()) &&
    !expired
  );
};

export const getVoucherCodeDisplay = ({
  dealBundleParent,
  voucherCode,
  flightIntegration,
  giftCard,
  orderLineStatus,
  expired,
}) => {
  const redeemable = isVoucherRedeemable({ expired, orderLineStatus });
  if (voucherCode && redeemable && !flightIntegration && !giftCard) {
    if (dealBundleParent) {
      return CLICK_REDEEM_IT;
    }

    return voucherCode;
  } else {
    return '';
  }
};

export const getVoucherPassenger = ({ passengerDetails }) => {
  if (!passengerDetails) {
    return '';
  }

  const { title, firstname, lastname } = passengerDetails;

  return [title, firstname, lastname].filter((entry) => entry).join(' ');
};

export const isNonBuyAvailable = ({ voucherSentDate }) => {
  return Boolean(voucherSentDate);
};

export const showOnlyGetWowcher = ({ orderLineStatus, expired }) => {
  // from angular
  return orderLineStatus.toLowerCase() === 'redeemed' && !expired;
};

export const showRedeem = (voucher) => {
  // from angular
  return (
    !voucher.flightIntegration &&
    (voucher.printable || isNonBuyAvailable(voucher)) &&
    !voucher.giftCard &&
    isVoucherRedeemable(voucher) &&
    !showOnlyGetWowcher(voucher)
  );
};

export const showInlineGetButton = (voucher) => {
  if (SHOW_GET_VOUCHER_BUTTON) {
    return true;
  }
  if (voucher.displayGetVoucher) {
    return true;
  } else {
    return (
      !voucher.flightIntegration &&
      (voucher.buyNow || isNonBuyAvailable(voucher)) &&
      (voucher.printable || isNonBuyAvailable(voucher)) &&
      !voucher.giftCard &&
      isVoucherRedeemable(voucher) &&
      showOnlyGetWowcher(voucher)
    );
  }
};

export const showViewProgress = (voucher) => {
  return voucher.deliveryNotificationAvailable;
};

export const showNeedHelp = (voucher) => {
  if (
    voucher.deliveryStatus === 'Returned to the Merchant' ||
    voucher.deliveryStatus === 'Return In Transit' ||
    voucher.deliveryStatus === 'Return Requested'
  ) {
    return true;
  }
  if (
    voucher.orderLineStatus.toLowerCase() === 'redeemed' &&
    voucher?.estimatedDeliveryDate
  ) {
    const diff = dayjs(voucher?.deliveredDate || voucher.estimatedDeliveryDate)
      .add(14, 'day')
      .diff(dayjs(), 'day');

    return diff >= 0;
  }

  return false;
};

export const isReturnInProgress = ({ deliveryStatus } = {}) => {
  const returnStatuses = [
    'Returned to the Merchant',
    'Return In Transit',
    'Return Requested',
  ];

  return returnStatuses.includes(deliveryStatus);
};

export const showNeedReturns = (voucher) => {
  const deliveredDate = voucher?.deliveredDate && dayjs(voucher.deliveredDate);
  const estimatedDeliveryDate =
    voucher?.estimatedDeliveryDate && dayjs(voucher.estimatedDeliveryDate);

  if (deliveredDate?.isBefore() || estimatedDeliveryDate?.isBefore()) {
    return true;
  }

  if (isReturnInProgress(voucher)) {
    return true;
  }

  return false;
};

export const getProgressStepFromVoucher = (voucher) => {
  const status = getVoucherStatus(voucher);
  switch (status) {
    case 'redeemed':
      return 1;
    case 'processing order':
      return 2;
    case 'dispatched':
      return 3;
    case 'delivered':
      return 4;
    case 'Ready to redeem':
    default:
      return 0;
  }
};

export const getShowTrackingButton = (voucher) => {
  return voucher.trackingUrl && voucher.showTrackingToCustomer;
};

export const getShowTrackingReference = (voucher) => {
  return voucher.trackingReference && voucher.showTrackingToCustomer;
};

export const getShowTip = (voucher) => {
  return (
    voucher.deliveryNotificationTooltipVisible &&
    voucher.estimatedDeliveryDate &&
    !voucher.speedyDelivery &&
    voucher.deliveryStatus !== 'delivered'
  );
};

export const getShowCreditUnredeemsCheckbox = (voucher) => {
  if (voucher.orderLineStatus === 'payment_captured') {
    return (
      voucher.businessAcceptsCreditUnredeems ||
      voucher.customerHasChosenCreditUnredeems
    );
  }

  return false;
};

export const showGiftOptions = (voucher) => {
  return (
    isVoucherRedeemable(voucher) &&
    voucher.giftable &&
    voucher.giftType !== 'wrapped'
  );
};

// export const isGiftPack = (giftType) => {
//   return (
//     giftType === GIFTING_OPTIONS.NEXT_DAY_GIFT_PACK ||
//     giftType === GIFTING_OPTIONS.STANDARD_DELIVERY_GIFT_PACK ||
//     giftType === GIFTING_OPTIONS.GIFT_PACK
//   );
// };
export const isNotYetRedeemed = (voucher) => {
  return voucher.orderLineStatus === 'payment_captured';
};

/* returns date if the gifting email is scheduled for sending in the fututre */
export const getGiftEmailSendDate = (voucher) => {
  if (!voucher.gifted || voucher.buyNow || !voucher.gift) return null;

  const beginingOfToday = dayjs().hour(0).minute(0).second(0).millisecond(0);
  const sendDate = dayjs(voucher.gift.scheduledVoucherSendDate);
  const diff = sendDate.diff(beginingOfToday);

  if (diff >= 0) {
    return sendDate.format('DD/MM/YYYY');
  } else {
    return null;
  }
};

/* returns date if the gifting email is scheduled for sending in the fututre */
export const trackNewVouchers = (order) => {
  if (!order.orderId) return null;

  // track awin
  if (!isNewPurchasesAffiliate()) {
    return trackPurchaseAffiliate({
      deals: order.deals,
      orderId: order.orderId,
      products: order.products,
    });
  }

  return trackPurchaseAffiliate({
    deals: order.deals,
    isFirstPurchase: isNewClient(),
    orderId: order.orderId,
    products: order.products,
  });
};

// Delay transactional emails from 2nd checkout and send all emails when 2nd checkout is over
export const delayTransactionalEmails = async (orderId) => {
  const { data } = await axios(
    `${process.env.NEXT_PUBLIC_PUBLIC_API}/v1/email-order/${orderId}`,
    {
      headers: httpCommonHeaders(),
      method: 'GET',
      withCredentials: true,
    },
  );

  return data || [];
};

// Get protect my voucher refund data
export const getVoucherRefundData = async (voucherCode) => {
  try {
    const response = await axios({
      headers: httpCommonHeaders(),
      method: 'GET',
      url: `${URL_PROTECT_MY_VOUCHER_REFUND_DATA}/${voucherCode}`,
    });
    if (response?.data) {
      response.data.amount = response.data.amount.toFixed(2);

      return response?.data;
    }

    return undefined;
  } catch (error) {
    console.warn(error);

    return undefined;
  }
};

// Send protect my voucher refund data
export const voucherRefundHandle = async (
  voucherCode,
  refundReason,
  isCashRefund,
) => {
  const defaultData = {
    protectMyVoucherRefundRequest: {
      cashRefund: isCashRefund,
      refundReasonId: refundReason || '',
    },
  };
  try {
    const response = await axios({
      data: defaultData,
      headers: httpCommonHeaders(),
      method: 'POST',
      url: `${URL_PROTECT_MY_VOUCHER_REFUND_DATA}/${voucherCode}`,
    });
    if (response?.status === 200) {
      return true;
    }

    return false;
  } catch (error) {
    console.warn(error);

    return undefined;
  }
};

export const checkProtectMyVoucherStatus = (status, dispatch) => {
  if (status !== 'Available' && status !== 'Refunded')
    dispatch({ type: 'REFUND_NOT_AVAILABLE' });
  else if (status === 'Available') dispatch({ type: 'REFUND_AVAILABLE' });
  else if (status === 'Refunded') dispatch({ type: 'VOUCHER_REFUNDED' });
};
